import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Text,
  Button,
  Checkbox,
  useToast,
  useBreakpointValue,
} from '@chakra-ui/react';

const NDAModal = ({ isOpen, onClose, onAccept, accept = true }) => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(true);
  const [isChecked, setIsChecked] = useState(true);
  const contentRef = useRef(null);
  const toast = useToast();

  const modalWidth = useBreakpointValue({
    base: '95%',
    md: '90%',
    lg: '800px',
  });
  const contentHeight = useBreakpointValue({ base: '50vh', md: '60vh' });

  useEffect(() => {
    if (!isOpen) {
      setHasScrolledToBottom(false);
      setIsChecked(false);
    }

    if (isOpen && accept) {
      setHasScrolledToBottom(true);
      setIsChecked(true);
    }
  }, [isOpen]);

  const handleScroll = (e) => {
    const element = e.target;
    if (element.scrollHeight - element.scrollTop <= element.clientHeight + 1) {
      setHasScrolledToBottom(true);
    }
  };

  const handleAccept = () => {
    if (!hasScrolledToBottom || !isChecked) {
      toast({
        title: 'Please read the entire agreement',
        description: 'Scroll to the bottom and check the box to proceed',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    onAccept();
  };

  if (!isOpen) return null;

  return (
    <Box
      position="fixed"
      top="0"
      left="0"
      right="0"
      bottom="0"
      bg="rgba(0, 0, 0, 0.7)"
      zIndex="1000"
      display="flex"
      alignItems="center"
      justifyContent="center"
      px={4}
    >
      <Box
        bg="white"
        borderRadius="xl"
        width={modalWidth}
        maxHeight="90vh"
        position="relative"
        p={{ base: 4, md: 6 }}
        overflowX="hidden"
        className="no-scrollbar"
      >
        <Text
          fontSize={{ base: 'lg', md: '2xl' }}
          fontWeight="bold"
          color="#2F4B6E"
          mb={4}
        >
          Seed A Founder Investor Non-Disclosure Agreement (NDA)
        </Text>

        <Box
          ref={contentRef}
          onScroll={handleScroll}
          height={contentHeight}
          overflowY="auto"
          mb={6}
          p={4}
          bg="#F4F7FD"
          borderRadius="md"
          css={{
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#888',
              borderRadius: '4px',
            },
          }}
          className="hide-scroll"
        >
          <Text whiteSpace="pre-line" fontSize="sm">
            {`By electronically signing this Agreement, you ("Investor" or "you") agree to the terms set out below with Seed A Founder Ltd ("Seed A Founder", "we", or "us"), on behalf of all startup founders and companies using our platform ("Founders").

1. Purpose
You are receiving access to confidential pitch decks, business information, and other materials through Seed A Founder for the purpose of evaluating potential startup investments.

2. Confidential Information
You agree to keep all non-public information you access confidential. This includes, but is not limited to:

• Pitch decks and business plans
• Financials, product info, or growth strategies
• Founder identities and team details
• Any material marked or reasonably understood to be confidential

3. Your Obligations
By signing this NDA, you agree to:

• Not share or disclose any confidential information to anyone else
• Only use the information to assess potential investments
• Take reasonable steps to protect the confidentiality of the materials
• Ensure any internal team members who access the platform also comply with this NDA

4. What’s Not Confidential
This NDA does not cover information that:

• Was already lawfully known to you
• Becomes public through no fault of yours
• Is shared with your written permission
• Is developed independently without access to the platform’s materials

5. Term
This NDA is valid for 3 years from the date you sign electronically.

6. Return or Deletion of Information
If requested, you will promptly delete or return all confidential materials received via Seed A Founder.

7. No License or Obligation
Access to materials does not give you ownership or rights over any intellectual property. This NDA also does not guarantee any investment opportunity or partnership.

8. No Circumvention
You agree not to bypass Seed A Founder or reach out to Founders directly for investment or business deals without written approval from us.

9. Enforcement
You understand that a breach of this NDA may cause serious harm. If this happens, Seed A Founder and/or the Founders may seek legal action, including injunctive relief.

10. Governing Law
This NDA is governed by the laws of [Insert Jurisdiction].

By clicking "I Agree" or submitting your electronic signature below, you acknowledge that you have read, understood, and agreed to be legally bound by this Non-Disclosure Agreement.`}
          </Text>
        </Box>

        <Box mb={4}>
          <Checkbox
            isDisabled={!hasScrolledToBottom || accept}
            isChecked={isChecked}
            onChange={(e) => setIsChecked(e.target.checked)}
            colorScheme="blue"
          >
            <Text fontSize="sm">
              I have read and agree to the Seed A Founder NDA
            </Text>
          </Checkbox>
        </Box>

        <Box
          display="flex"
          flexDirection={{ base: 'column', sm: 'row' }}
          justifyContent="flex-end"
          gap={4}
        >
          <Button
            variant="outline"
            onClick={onClose}
            width={{ base: '100%', sm: 'auto' }}
          >
            Close
          </Button>

          {!accept ? (
            <Button
              bg="#134A70"
              color="white"
              _hover={{ bg: '#0d3654' }}
              onClick={handleAccept}
              isDisabled={!hasScrolledToBottom || !isChecked}
              width={{ base: '100%', sm: 'auto' }}
            >
              Agree and Continue
            </Button>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};

export default NDAModal;
